// The shell's own screens: route skeleton, not found, screen error, offline and pending account.
import { useQueryClient } from '@tanstack/react-query'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { Compass, Hourglass, ServerOff, TriangleAlert, WifiOff } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { sessionQuery } from '../lib/session.ts'
import { Button } from '../kit/Button.tsx'
import { EmptyState } from '../kit/EmptyState.tsx'
import { Screen } from '../kit/layout.tsx'
import { Skeleton, SkeletonRows } from '../kit/Skeleton.tsx'
import { useSignOut } from './shell/UserMenu.tsx'

/** Loading state of a screen. The router shows it after 300 ms (defaultPendingMs), so it draws at once then. */
export function RouteSkeleton({ delay }: { delay?: number }) {
  const t = useT()
  return (
    <div aria-busy="true" aria-label={t('common.loading')} className="mx-auto w-full max-w-page px-4 pt-5 md:px-8 md:pt-7">
      <Skeleton delay={delay} className="mb-3 h-3 w-24" />
      <Skeleton delay={delay} className="mb-8 h-8 w-64" />
      <SkeletonRows delay={delay} rows={8} />
    </div>
  )
}

export function NotFoundScreen() {
  const t = useT()
  const router = useRouter()
  return (
    <Screen name="not-found">
      <EmptyState
        heading="h1"
        icon={Compass}
        title={t('common.notFound.title')}
        body={t('common.notFound.body')}
        action={
          <Button variant="primary" onClick={() => void router.navigate({ to: '/' })}>
            {t('common.goHome')}
          </Button>
        }
      />
    </Screen>
  )
}

/** The router's error component: a screen threw while loading or rendering. */
export function ErrorScreen({ error, reset }: ErrorComponentProps) {
  const t = useT()
  const router = useRouter()
  if (import.meta.env.DEV) console.warn('Screen failed', error)
  return (
    <Screen name="error">
      <EmptyState
        heading="h1"
        icon={TriangleAlert}
        tone="danger"
        title={t('common.screenError.title')}
        body={t('common.screenError.body')}
        action={
          <>
            <Button
              variant="primary"
              onClick={() => {
                reset()
                void router.invalidate()
              }}
            >
              {t('common.retry')}
            </Button>
            <Button onClick={() => void router.navigate({ to: '/' })}>{t('common.goHome')}</Button>
          </>
        }
      />
    </Screen>
  )
}

/** /api/me could not be reached: the shell renders from the service worker's cache, with no data. `unavailable`: the device is online and the server did not answer properly. */
export function OfflineScreen({ unavailable }: { unavailable?: boolean }) {
  const t = useT()
  const queryClient = useQueryClient()
  const router = useRouter()
  return (
    <Screen name={unavailable ? 'unavailable' : 'offline'}>
      <EmptyState
        heading="h1"
        icon={unavailable ? ServerOff : WifiOff}
        title={t(unavailable ? 'common.unavailable.title' : 'common.offline.title')}
        body={t(unavailable ? 'common.unavailable.body' : 'common.offline.body')}
        action={
          <Button
            variant="primary"
            onClick={async () => {
              await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey })
              await router.invalidate()
            }}
          >
            {t('common.retry')}
          </Button>
        }
      />
    </Screen>
  )
}

/** A signed-in account the owner has not approved yet (the API answers 403 account_pending to everything else). */
export function PendingScreen() {
  const t = useT()
  const signOut = useSignOut()
  return (
    <Screen name="pending">
      <EmptyState
        heading="h1"
        icon={Hourglass}
        title={t('error.account_pending')}
        body={t('common.pending.body')}
        action={<Button onClick={() => void signOut()}>{t('common.account.signOut')}</Button>}
      />
    </Screen>
  )
}
